import { supabase } from '../lib/supabase';

/**
 * Service for admin operations on use cases, AI tools, and prompt instructions
 * Handles snake_case (database) to camelCase (React) conversion
 */
export const adminService = {
  /**
   * Get statistics for admin dashboard
   * @returns {Promise<Object>} Dashboard statistics
   */
  async getStatistics() {
    try {
      const [useCasesCount, aiToolsCount, promptFieldsCount] = await Promise.all([
        supabase?.from('use_cases')?.select('id', { count: 'exact', head: true }),
        supabase?.from('ai_tools')?.select('id', { count: 'exact', head: true }),
        supabase?.from('prompt_fields')?.select('id', { count: 'exact', head: true })
      ]);

      return {
        useCases: useCasesCount?.count || 0,
        aiTools: aiToolsCount?.count || 0,
        promptFields: promptFieldsCount?.count || 0
      };
    } catch (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }
  },

  /**
   * Get all use cases with their AI tools
   * @returns {Promise<Array>} Use cases with nested AI tools
   */
  async getUseCasesWithTools() {
    const { data, error } = await supabase?.from('use_cases')?.select(`
        id,
        name,
        description,
        icon,
        created_at,
        use_case_ai_tools (
          ai_tools (
            id,
            name,
            description,
            value,
            created_at
          )
        )
      `)?.order('name', { ascending: true });

    if (error) throw error;

    return data?.map(useCase => ({
      id: useCase?.id,
      name: useCase?.name,
      description: useCase?.description,
      icon: useCase?.icon,
      createdAt: useCase?.created_at,
      aiTools: useCase?.use_case_ai_tools?.map(relation => ({
        id: relation?.ai_tools?.id,
        name: relation?.ai_tools?.name,
        description: relation?.ai_tools?.description,
        value: relation?.ai_tools?.value,
        createdAt: relation?.ai_tools?.created_at
      })) || []
    })) || [];
  },

  /**
   * Get all AI tools with their prompt instructions
   * @returns {Promise<Array>} AI tools with nested prompt instructions
   */
  async getAIToolsWithInstructions() {
    const { data, error } = await supabase?.from('ai_tools')?.select(`
        id,
        name,
        description,
        value,
        created_at,
        ai_tool_prompt_instructions (
          is_required,
          display_order,
          prompt_instructions (
            id,
            name,
            description,
            field_type,
            is_required,
            display_order,
            placeholder
          )
        )
      `)?.order('name', { ascending: true });

    if (error) throw error;

    return data?.map(tool => ({
      id: tool?.id,
      name: tool?.name,
      description: tool?.description,
      value: tool?.value,
      createdAt: tool?.created_at,
      promptInstructions: tool?.ai_tool_prompt_instructions?.map(relation => ({
        id: relation?.prompt_instructions?.id,
        name: relation?.prompt_instructions?.name,
        description: relation?.prompt_instructions?.description,
        fieldType: relation?.prompt_instructions?.field_type,
        isRequired: relation?.is_required,
        displayOrder: relation?.display_order,
        placeholder: relation?.prompt_instructions?.placeholder
      }))?.sort((a, b) => a?.displayOrder - b?.displayOrder) || []
    })) || [];
  },

  /**
   * Create a new use case
   * @param {Object} useCase - Use case data
   * @returns {Promise<Object>} Created use case
   */
  async createUseCase(useCase) {
    const { data, error } = await supabase?.from('use_cases')?.insert({
      name: useCase?.name,
      description: useCase?.description,
      icon: useCase?.icon
    })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      name: data?.name,
      description: data?.description,
      icon: data?.icon,
      createdAt: data?.created_at
    };
  },

  /**
   * Update a use case
   * @param {string} id - Use case ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated use case
   */
  async updateUseCase(id, updates) {
    const { data, error } = await supabase?.from('use_cases')?.update({
      name: updates?.name,
      description: updates?.description,
      icon: updates?.icon
    })?.eq('id', id)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      name: data?.name,
      description: data?.description,
      icon: data?.icon,
      createdAt: data?.created_at
    };
  },

  /**
   * Delete a use case
   * @param {string} id - Use case ID
   * @returns {Promise<void>}
   */
  async deleteUseCase(id) {
    const { error } = await supabase?.from('use_cases')?.delete()?.eq('id', id);
    if (error) throw error;
  },

  /**
   * Create a new AI tool
   * @param {Object} aiTool - AI tool data
   * @returns {Promise<Object>} Created AI tool
   */
  async createAITool(aiTool) {
    const { data, error } = await supabase?.from('ai_tools')?.insert({
      name: aiTool?.name,
      description: aiTool?.description,
      value: aiTool?.value
    })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      name: data?.name,
      description: data?.description,
      value: data?.value,
      createdAt: data?.created_at
    };
  },

  /**
   * Update an AI tool
   * @param {string} id - AI tool ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated AI tool
   */
  async updateAITool(id, updates) {
    const { data, error } = await supabase?.from('ai_tools')?.update({
      name: updates?.name,
      description: updates?.description,
      value: updates?.value
    })?.eq('id', id)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      name: data?.name,
      description: data?.description,
      value: data?.value,
      createdAt: data?.created_at
    };
  },

  /**
   * Delete an AI tool
   * @param {string} id - AI tool ID
   * @returns {Promise<void>}
   */
  async deleteAITool(id) {
    const { error } = await supabase?.from('ai_tools')?.delete()?.eq('id', id);
    if (error) throw error;
  },

  /**
   * Create a new prompt instruction
   * @param {Object} instruction - Prompt instruction data
   * @returns {Promise<Object>} Created prompt instruction
   */
  async createPromptInstruction(instruction) {
    const { data, error } = await supabase?.from('prompt_instructions')?.insert({
      name: instruction?.name,
      description: instruction?.description,
      field_type: instruction?.fieldType,
      is_required: instruction?.isRequired,
      display_order: instruction?.displayOrder,
      placeholder: instruction?.placeholder
    })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      name: data?.name,
      description: data?.description,
      fieldType: data?.field_type,
      isRequired: data?.is_required,
      displayOrder: data?.display_order,
      placeholder: data?.placeholder,
      createdAt: data?.created_at
    };
  },

  /**
   * Update a prompt instruction
   * @param {string} id - Prompt instruction ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated prompt instruction
   */
  async updatePromptInstruction(id, updates) {
    const { data, error } = await supabase?.from('prompt_instructions')?.update({
      name: updates?.name,
      description: updates?.description,
      field_type: updates?.fieldType,
      is_required: updates?.isRequired,
      display_order: updates?.displayOrder,
      placeholder: updates?.placeholder
    })?.eq('id', id)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      name: data?.name,
      description: data?.description,
      fieldType: data?.field_type,
      isRequired: data?.is_required,
      displayOrder: data?.display_order,
      placeholder: data?.placeholder,
      createdAt: data?.created_at
    };
  },

  /**
   * Delete a prompt instruction
   * @param {string} id - Prompt instruction ID
   * @returns {Promise<void>}
   */
  async deletePromptInstruction(id) {
    const { error } = await supabase?.from('prompt_instructions')?.delete()?.eq('id', id);
    if (error) throw error;
  },

  /**
   * Link use case to AI tool
   * @param {string} useCaseId - Use case ID
   * @param {string} aiToolId - AI tool ID
   * @returns {Promise<void>}
   */
  async linkUseCaseToAITool(useCaseId, aiToolId) {
    const { error } = await supabase?.from('use_case_ai_tools')?.insert({
      use_case_id: useCaseId,
      ai_tool_id: aiToolId
    });

    if (error) throw error;
  },

  /**
   * Unlink use case from AI tool
   * @param {string} useCaseId - Use case ID
   * @param {string} aiToolId - AI tool ID
   * @returns {Promise<void>}
   */
  async unlinkUseCaseFromAITool(useCaseId, aiToolId) {
    const { error } = await supabase?.from('use_case_ai_tools')?.delete()?.eq('use_case_id', useCaseId)?.eq('ai_tool_id', aiToolId);

    if (error) throw error;
  },

  /**
   * Link AI tool to prompt instruction
   * @param {string} aiToolId - AI tool ID
   * @param {string} promptInstructionId - Prompt instruction ID
   * @param {Object} options - Additional options (isRequired, displayOrder)
   * @returns {Promise<void>}
   */
  async linkAIToolToPromptInstruction(aiToolId, promptInstructionId, options = {}) {
    const { error } = await supabase?.from('ai_tool_prompt_instructions')?.insert({
      ai_tool_id: aiToolId,
      prompt_instruction_id: promptInstructionId,
      is_required: options?.isRequired || false,
      display_order: options?.displayOrder || 0
    });

    if (error) throw error;
  },

  /**
   * Unlink AI tool from prompt instruction
   * @param {string} aiToolId - AI tool ID
   * @param {string} promptInstructionId - Prompt instruction ID
   * @returns {Promise<void>}
   */
  async unlinkAIToolFromPromptInstruction(aiToolId, promptInstructionId) {
    const { error } = await supabase?.from('ai_tool_prompt_instructions')?.delete()?.eq('ai_tool_id', aiToolId)?.eq('prompt_instruction_id', promptInstructionId);

    if (error) throw error;
  },

  /**
   * Get all prompt instructions
   * @returns {Promise<Array>} All prompt instructions
   */
  async getAllPromptInstructions() {
    const { data, error } = await supabase?.from('prompt_instructions')?.select('*')?.order('display_order', { ascending: true });

    if (error) throw error;

    return data?.map(instruction => ({
      id: instruction?.id,
      name: instruction?.name,
      description: instruction?.description,
      fieldType: instruction?.field_type,
      isRequired: instruction?.is_required,
      displayOrder: instruction?.display_order,
      placeholder: instruction?.placeholder,
      createdAt: instruction?.created_at
    })) || [];
  }
};